import {
    MAX_RAM_STICKS,
    getRamStickCount,
    isModuleUnlocked,
    unlockModules,
    unlockRAMUpgrade,
} from "../systems/memory.js";
import { setLight, removeLight } from "../systems/lighting.js";
import { getNearestRoom, getRoomAt } from "../systems/rooms.js";
import { scheduleRamDropNarration, scheduleRamPickupNarration } from "../systems/roomNarration.js";
import { AUDIO_SOUNDS, playAt } from "../systems/audio.js";
import { openMemoryUI } from "../ui/memoryUI.js";

const RAM_PICKUP_Z = 9706;
const RAM_PICKUP_SCALE = 1.35;
const RAM_PICKUP_RADIUS = 24;
const RAM_PICKUP_BOB_PIXELS = 7;
const FIRST_RAM_MODULES = ["action_bite", "action_bark"];
let ramPickupSeq = 0;

function hasFirstRamReward() {
    return getRamStickCount() > 1
        && FIRST_RAM_MODULES.every((id) => isModuleUnlocked(id));
}

export function spawnRamStickPickup(worldPos, options = {}) {
    if (!worldPos) return null;
    if (options.shouldSpawn && !options.shouldSpawn()) return null;

    const pickupKey = options.pickupKey || `ram_pickup_${ramPickupSeq++}`;
    if (get("ram_pickup", { recursive: true }).some((p) => p.pickupKey === pickupKey)) return null;
    const lightId = `${pickupKey}_glow`;
    const unlockModuleIds = options.unlockModuleIds || [];
    const targetRamCount = Math.max(1, Math.min(MAX_RAM_STICKS, options.targetRamCount || getRamStickCount() + 1));
    const onCollected = options.onCollected;
    const canCollect = options.canCollect || (() => true);

    const pickup = add([
        pos(worldPos),
        anchor("center"),
        area({
            // α.26+: areas need body or isSensor to be collision-eligible
            isSensor: true,
            shape: new Rect(
                vec2(-RAM_PICKUP_RADIUS, -RAM_PICKUP_RADIUS),
                RAM_PICKUP_RADIUS * 2,
                RAM_PICKUP_RADIUS * 2,
            ),
        }),
        z(RAM_PICKUP_Z),
        "ram_pickup",
        {
            pickupKey,
            collected: false,
            baseY: worldPos.y,
            age: 0,
        },
    ]);

    const glowOuter = pickup.add([
        circle(28),
        pos(0, 0),
        anchor("center"),
        color(170, 70, 255),
        opacity(0.22),
        z(RAM_PICKUP_Z - 1),
    ]);

    const glowCore = pickup.add([
        circle(18),
        pos(0, 0),
        anchor("center"),
        color(220, 150, 255),
        opacity(0.18),
        z(RAM_PICKUP_Z - 1),
    ]);

    const shadow = pickup.add([
        rect(30, 8, { radius: 4 }),
        pos(0, 13),
        anchor("center"),
        color(0, 0, 0),
        opacity(0.28),
        z(RAM_PICKUP_Z - 1),
    ]);

    pickup.add([
        sprite("ram_stick"),
        anchor("center"),
        scale(RAM_PICKUP_SCALE),
        z(RAM_PICKUP_Z),
    ]);

    function collect() {
        if (!canCollect()) return;
        if (pickup.collected) return;
        pickup.collected = true;
        playAt(AUDIO_SOUNDS.ramPickup, pickup.pos);
        removeLight(lightId);
        while (getRamStickCount() < targetRamCount) {
            unlockRAMUpgrade();
        }
        unlockModules(unlockModuleIds);
        openMemoryUI();
        if (onCollected) onCollected();
        scheduleRamPickupNarration();
        destroy(pickup);
    }

    pickup.onUpdate(() => {
        if (!pickup.exists()) return;
        pickup.age += dt();
        const bob = Math.sin(pickup.age * 4.4) * RAM_PICKUP_BOB_PIXELS;
        const pulse = 0.5 + Math.sin(pickup.age * 6.2) * 0.5;
        // α.25+: mutating pos.y in place does not update transforms — assign a new Vec2
        pickup.pos = vec2(pickup.pos.x, pickup.baseY + bob);

        glowOuter.opacity = 0.18 + pulse * 0.1;
        glowOuter.scale = vec2(1 + pulse * 0.12);
        glowCore.opacity = 0.14 + pulse * 0.08;
        glowCore.scale = vec2(1 + pulse * 0.08);
        shadow.opacity = 0.20 + (1 - Math.abs(bob) / RAM_PICKUP_BOB_PIXELS) * 0.12;
        shadow.scale = vec2(1 + Math.abs(bob) / RAM_PICKUP_BOB_PIXELS * 0.18, 1);

        const room = getRoomAt(pickup.pos) || getNearestRoom(pickup.pos);
        setLight(lightId, {
            pos: pickup.pos,
            radius: 86 + pulse * 10,
            color: [176, 76, 255],
            intensity: 0.75 + pulse * 0.18,
            pulse: false,
            roomId: room ? room.id : null,
        });

        const player = get("player")[0];
        if (player?.exists?.() && player.pos.dist(pickup.pos) <= RAM_PICKUP_RADIUS) {
            collect();
        }
    });

    pickup.onCollide("player", collect);
    pickup.onDestroy(() => removeLight(lightId));

    playAt(AUDIO_SOUNDS.ramDrop, worldPos);
    scheduleRamDropNarration();

    return pickup;
}

export function spawnFirstRamStickPickup(worldPos) {
    return spawnRamStickPickup(worldPos, {
        pickupKey: "first_ram_pickup",
        targetRamCount: 2,
        unlockModuleIds: FIRST_RAM_MODULES,
        shouldSpawn: () => !hasFirstRamReward(),
    });
}

export function spawnUpgradeRamStickPickup(worldPos, options = {}) {
    return spawnRamStickPickup(worldPos, {
        pickupKey: options.pickupKey || `upgrade_ram_pickup_${ramPickupSeq++}`,
        targetRamCount: options.targetRamCount || getRamStickCount() + 1,
        unlockModuleIds: options.unlockModuleIds || [],
        shouldSpawn: () => getRamStickCount() < MAX_RAM_STICKS,
        canCollect: options.canCollect,
        onCollected: options.onCollected,
    });
}

export function spawnStoryRamStickPickup(worldPos, options = {}) {
    return spawnRamStickPickup(worldPos, {
        pickupKey: "story_ram_pickup",
        targetRamCount: Math.min(MAX_RAM_STICKS, getRamStickCount() + 1),
        unlockModuleIds: [],
        shouldSpawn: () => getRamStickCount() < MAX_RAM_STICKS,
        canCollect: options.canCollect,
        onCollected: options.onCollected,
    });
}
